import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';

import {
  getCommonAmplitudeProperties,
  getSourceEventParams,
  useAmplitude,
} from '@crehana/analytics';
import { useMe } from '@crehana/auth';
import { Skeleton } from '@crehana/compass.ui';
import { cn } from '@crehana/compass.ui/v2';
import { boldFormatter } from '@crehana/i18n';
// biome-ignore lint/style/noRestrictedImports: legacy import kept for compatibility
import { Container, type CuiOptionType, Dropdown } from '@crehana/ui';
import { useUITheme } from '@crehana/web';
import type { MyDiplomasQueryVariables } from '@crehana/web/Graphql/Queries/types/MyDiplomasQuery';
import useMyDiplomas from '@crehana/web/HomeUser/Diplomas/hooks/useMyDiplomas';

import {
  Diplomas,
  type DiplomasProps,
  useParticipationCertificates,
} from '@/shared/components';
import {
  PageLayoutBreadcrumb,
  PageLayoutTop,
} from '@/shared/vendor/compass-web-page-layout';
import { useLearnDashboardContext } from '@/views/LearnDashboard/context';
import useLearnBreadcrumbTrail from '@/views/LearnDashboard/hooks/useLearnBreadcrumbTrail';

import { LEARN_DASHBOARD_PAGE_NAME } from '../../../constants';
import {
  ENROLLMENTS_DEGREES_ROUTE,
  PROJECTS_ROUTE,
} from '../../../routes/common.routes';
import { DIPLOMA_FILTER } from './constants';
import { useAddI18nLocales, useTranslation } from './i18n';
import type { MyDiplomasCustom } from './type';

const DiplomasSection: React.FC<DiplomasProps> = () => {
  useAddI18nLocales();

  const { logEvent } = useAmplitude();
  const { me } = useMe();
  const { t } = useTranslation();
  const { isDark } = useUITheme();
  const { items, isLoading: isBreadcrumbLoading } = useLearnBreadcrumbTrail();
  const { isLoadingPlayroomActivated, isPlayroomEnabled } =
    useLearnDashboardContext();

  const [actData, setActData] = useState<MyDiplomasCustom[]>([]);
  const [selectAct, setSelectAct] = useState<CuiOptionType<number>>();
  const [diplomaData, setDiplomaData] = useState<MyDiplomasCustom[]>();
  const [recordsData, setRecordsData] = useState<MyDiplomasCustom[]>();

  const isB2B = !!me?.isB2B;
  const organizationSlug = me?.organization?.slug;

  const {
    loading,
    error,
    refetch: refetchDiplomas,
    diplomas,
  } = useMyDiplomas({
    username: me?.username!,
  });

  const onRefetch = useCallback(
    (refetchVariables?: MyDiplomasQueryVariables) => {
      // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
      if (refetchDiplomas) return refetchDiplomas(refetchVariables);
    },
    [refetchDiplomas],
  );

  const {
    certificatesData: certificates,
    loading: loadingCertificates,
    refetch,
  } = useParticipationCertificates();

  const isLoading = loading || loadingCertificates;

  const OptionCustom: CuiOptionType<number>[] = useMemo(
    () => [
      {
        label: t('DOWNLOAD_FILTER_ALL', {
          interpolation: { count: diplomas.length + certificates.length },
        }),
        value: DIPLOMA_FILTER.ALL,
      },
      {
        label: t('DOWNLOAD_FILTER_DIPLOMA', {
          interpolation: { count: diplomas.length },
        }),
        value: DIPLOMA_FILTER.CERTIFICATIONS,
      },
      {
        label: t('DOWNLOAD_FILTER_PROOF', {
          interpolation: { count: certificates.length },
        }),
        value: DIPLOMA_FILTER.CONSTANCIES,
      },
    ],
    [t, diplomas.length, certificates.length],
  );

  useEffect(() => {
    // biome-ignore lint/style/useBlockStatements: concise single-line form preferred here
    if (!selectAct && !loading && !loadingCertificates)
      setSelectAct(OptionCustom[0]);
  }, [OptionCustom, loading, loadingCertificates, selectAct]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (diplomas) {
      setDiplomaData(
        diplomas.map(element => ({
          ...element,
          type: t('DOWNLOAD_DIPLOMA'),
        })),
      );
    }

    if (certificates) {
      setRecordsData(
        certificates.map(
          certificate =>
            ({
              ...certificate,
              type: t('DOWNLOAD_PROOF'),
            }) as unknown as MyDiplomasCustom,
        ),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diplomas.length, certificates.length]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useLayoutEffect(() => {
    if (diplomaData && recordsData) {
      if (!selectAct?.value || selectAct?.value === DIPLOMA_FILTER.ALL) {
        setActData(diplomaData.concat(recordsData));
      } else if (selectAct?.value === DIPLOMA_FILTER.CERTIFICATIONS) {
        onRefetch()?.then(() => {
          setActData(diplomaData);
        });
      } else if (selectAct?.value === DIPLOMA_FILTER.CONSTANCIES) {
        refetch().then(() => {
          setActData(recordsData);
        });
      }
    }
  }, [selectAct, diplomaData, recordsData, onRefetch, me?.username, refetch]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are intentionally managed
  useEffect(() => {
    if (!isLoadingPlayroomActivated) {
      logEvent({
        event: 'Render Home Certificates',
        data: {
          ...getSourceEventParams(),
          ...getCommonAmplitudeProperties(me!, isPlayroomEnabled),
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoadingPlayroomActivated, isPlayroomEnabled]);

  return (
    <>
      <PageLayoutTop className="tw-pb-0" transparent>
        <PageLayoutBreadcrumb items={items} isLoading={isBreadcrumbLoading} />
      </PageLayoutTop>
      <Container className="tw-my-2.4" type="dashboard">
        {isLoading ? (
          <Skeleton className="tw-mb-2 tw-w-[30%] tw-h-2" animated />
        ) : (
          <h3
            className={cn('mb-20 font-body3 ', {
              'text-white': isDark,
              'text-base-main': !isDark,
            })}
          >
            {t('DOWNLOAD_DESCRIPTION_GENERAL', {
              formatter: boldFormatter,
              interpolation: {
                diplomasCount: diplomas.length,
                certificateCount: certificates.length,
              },
            })}
          </h3>
        )}

        <Dropdown
          displayPosition="bottom"
          options={OptionCustom}
          hint={t('DOWNLOAD_FILTER')}
          selected={selectAct}
          onSelected={setSelectAct}
          size={4}
          isDark={isDark}
        />
      </Container>

      <Diplomas
        amplitudePageName={LEARN_DASHBOARD_PAGE_NAME}
        enrollmentsRoutePath={
          isB2B ? `/org/${organizationSlug}/learn/` : '/learn/'
        }
        enrollmentsDegreesRoutePath={ENROLLMENTS_DEGREES_ROUTE.path}
        projectRoutePath={PROJECTS_ROUTE.path}
        loading={isLoading}
        refetch={onRefetch}
        error={error}
        actData={actData}
      />
    </>
  );
};

export default DiplomasSection;
